import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { supabase } from '@sixam/supabase/client';

import { authedFetch } from '@/lib/authed-fetch';
import { getCurrentTime } from '@/lib/dev-clock';
import type { Event } from '@/lib/events';
import {
  buildEventUpdatePushPayload,
  reminderDateForEvent,
  type NotificationPermissionKind,
  type ReminderIntervalMinutes,
} from '@sixam/shared/notification-core';
import type { Database } from '@sixam/shared/database.types';

export type EventReminder = Database['public']['Tables']['event_reminders']['Row'];
export type EventNotificationLog = Database['public']['Tables']['event_notification_logs']['Row'];

async function parseApiResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = (await response.json().catch(() => null)) as ({ error?: string } & T) | null;

  if (!response.ok) {
    throw new Error(payload?.error ?? fallbackMessage);
  }

  return payload as T;
}

/** A scheduled reminder together with the run it belongs to. */
export type UpcomingReminder = EventReminder & {
  event: Pick<Event, 'id' | 'place_name' | 'starts_at' | 'title'> | null;
};

/**
 * Every reminder this member has set for a run that has not started yet. Read
 * straight from the table — `event_reminders_select_own` already scopes rows to
 * the signed-in member.
 */
export async function fetchMyUpcomingReminders(): Promise<UpcomingReminder[]> {
  const { data, error } = await supabase
    .from('event_reminders')
    .select('*, event:events(id, title, starts_at, place_name)')
    .eq('status', 'scheduled')
    .gte('remind_at', getCurrentTime().toISOString())
    .order('remind_at', { ascending: true });

  if (error) {
    throw error;
  }
  return (data ?? []) as UpcomingReminder[];
}

export async function fetchMyEventReminder(eventId: string): Promise<EventReminder | null> {
  const response = await authedFetch(`/api/events/${encodeURIComponent(eventId)}/reminders`);
  return parseApiResponse<EventReminder | null>(response, 'Could not load reminder.');
}

export async function saveEventReminder(payload: {
  eventId: string;
  localNotificationId?: string | null;
  remindAt: string;
  tenantId: string;
}): Promise<EventReminder> {
  const response = await authedFetch(
    `/api/events/${encodeURIComponent(payload.eventId)}/reminders`,
    {
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
      method: 'PUT',
    }
  );
  return parseApiResponse<EventReminder>(response, 'Could not save reminder.');
}

export async function cancelEventReminder(eventId: string): Promise<{ status: 'ok' }> {
  const response = await authedFetch(`/api/events/${encodeURIComponent(eventId)}/reminders`, {
    method: 'DELETE',
  });
  return parseApiResponse<{ status: 'ok' }>(response, 'Could not cancel reminder.');
}

/** Android needs a channel before anything can present as a heads-up notification. */
export const EVENT_REMINDER_CHANNEL_ID = 'event-reminders';

type ExpoNotifications = typeof import('expo-notifications');

async function ensureAndroidChannels(Notifications: ExpoNotifications): Promise<void> {
  if (Platform.OS !== 'android') {
    return;
  }

  await Notifications.setNotificationChannelAsync('event-updates', {
    importance: Notifications.AndroidImportance.MAX,
    name: 'Event updates',
    vibrationPattern: [0, 220, 160, 220],
  });
  await Notifications.setNotificationChannelAsync(EVENT_REMINDER_CHANNEL_ID, {
    importance: Notifications.AndroidImportance.MAX,
    name: 'Event reminders',
    vibrationPattern: [0, 220, 160, 220],
  });
}

/**
 * Ensures channels exist and returns the effective permission, requesting it if
 * it has not been asked for yet. Scheduling without this silently succeeds while
 * nothing is ever shown to the runner.
 */
export async function ensureNotificationPermission(): Promise<NotificationPermissionKind> {
  if (Platform.OS === 'web') {
    return 'unsupported';
  }

  const Notifications = await import('expo-notifications');
  await ensureAndroidChannels(Notifications);

  const existing = await Notifications.getPermissionsAsync();
  let finalStatus = existing.status;
  let iosStatus = existing.ios?.status;

  const isProvisionalStatus = (status: number | undefined) =>
    Platform.OS === 'ios' &&
    status != null &&
    (status === Notifications.IosAuthorizationStatus.PROVISIONAL ||
      status === Notifications.IosAuthorizationStatus.EPHEMERAL);

  if (existing.status !== 'granted' && !isProvisionalStatus(iosStatus)) {
    const requested = await Notifications.requestPermissionsAsync();
    finalStatus = requested.status;
    iosStatus = requested.ios?.status;
  }

  if (isProvisionalStatus(iosStatus)) {
    return 'provisional';
  }
  return finalStatus === 'granted' ? 'granted' : 'denied';
}

export async function registerExpoPushToken(tenantId: string): Promise<{
  permission: NotificationPermissionKind;
  token?: string;
}> {
  if (Platform.OS === 'web') {
    return { permission: 'unsupported' };
  }

  const Notifications = await import('expo-notifications');
  const permission = await ensureNotificationPermission();

  if (permission !== 'granted' && permission !== 'provisional') {
    return { permission: 'denied' };
  }

  const isProvisional = permission === 'provisional';
  const projectId = Constants.expoConfig?.extra?.eas?.projectId ?? Constants.easConfig?.projectId;
  if (!projectId) {
    throw new Error('EAS project ID is required for Expo push tokens.');
  }

  const token = (await Notifications.getExpoPushTokenAsync({ projectId })).data;
  const response = await authedFetch('/api/notifications/tokens', {
    body: JSON.stringify({
      platform: Platform.OS,
      tenantId,
      token,
    }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

  await parseApiResponse<{ status: 'ok' }>(response, 'Could not save push token.');
  return { permission: isProvisional ? 'provisional' : 'granted', token };
}

export async function scheduleLocalEventReminder(
  event: Pick<Event, 'id' | 'tenant_id' | 'title' | 'starts_at' | 'place_name'>,
  tenantSlug: string,
  intervalMinutes: ReminderIntervalMinutes,
  timeZone?: string
): Promise<EventReminder> {
  if (!event.tenant_id) {
    throw new Error('Personal events do not support reminders.');
  }
  if (Platform.OS === 'web') {
    throw new Error('Local reminders are available in the native app.');
  }

  const Notifications = await import('expo-notifications');
  const remindAt = reminderDateForEvent(event.starts_at, intervalMinutes, getCurrentTime());

  // Without this the schedule call still resolves and we would store a reminder
  // that can never be shown, which reads to the runner as a reminder that simply
  // never arrived.
  const permission = await ensureNotificationPermission();
  if (permission !== 'granted' && permission !== 'provisional') {
    throw new Error('Allow notifications for 6am BKK in your device settings to get reminders.');
  }

  const payload = buildEventUpdatePushPayload(event, tenantSlug, timeZone);
  const localNotificationId = await Notifications.scheduleNotificationAsync({
    content: {
      body: `${event.place_name} starts soon.`,
      data: payload.data,
      title: event.title,
    },
    trigger: {
      channelId: EVENT_REMINDER_CHANNEL_ID,
      date: remindAt,
      type: Notifications.SchedulableTriggerInputTypes.DATE,
    },
  });

  return saveEventReminder({
    eventId: event.id,
    localNotificationId,
    remindAt: remindAt.toISOString(),
    tenantId: event.tenant_id,
  });
}

export async function cancelLocalEventReminder(reminder: EventReminder): Promise<void> {
  if (Platform.OS !== 'web' && reminder.local_notification_id) {
    const Notifications = await import('expo-notifications');
    await Notifications.cancelScheduledNotificationAsync(reminder.local_notification_id);
  }
  await cancelEventReminder(reminder.event_id);
}

export async function sendAdminEventUpdateNotification(
  tenantSlug: string,
  eventId: string
): Promise<EventNotificationLog> {
  const response = await authedFetch(
    `/api/admin/${encodeURIComponent(tenantSlug)}/events/${encodeURIComponent(eventId)}/notifications`,
    { method: 'POST' }
  );
  return parseApiResponse<EventNotificationLog>(response, 'Could not send event notification.');
}
